// Hydra analytics client — wraps /api/analytics/* on the Hydra Fastify server.
// Auth is a single shared bearer token (separate from the consumer WS bearer).
// Spec: hydra/docs/G1_ANALYTICS_INTEGRATION.md.

import { toHydraGameKey } from './analyticsGameKey';

const BASE = import.meta.env.VITE_HYDRA_ANALYTICS_URL as string | undefined;
const TOKEN = import.meta.env.VITE_HYDRA_ANALYTICS_TOKEN as string | undefined;

export function hasAnalytics(): boolean {
  return typeof BASE === 'string' && BASE.length > 0;
}

export class AnalyticsError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'AnalyticsError';
    this.status = status;
    this.code = code;
  }
}

export async function fetchAnalytics<T>(path: string, signal?: AbortSignal): Promise<T> {
  if (!BASE) throw new AnalyticsError('VITE_HYDRA_ANALYTICS_URL not set', 0, 'NO_BASE');
  const url = `${BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
  const headers: HeadersInit = TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {};
  const res = await fetch(url, { signal, headers });
  if (!res.ok) {
    let code: string | undefined;
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string; code?: string };
      if (body?.error) message = body.error;
      if (body?.code) code = body.code;
    } catch {
      // non-JSON error body — fall back to status line
    }
    throw new AnalyticsError(message, res.status, code);
  }
  return (await res.json()) as T;
}

// ─── Response shapes ────────────────────────────────────────────────

export interface AnalyticsListEnvelope<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface AnalyticsAggregateEnvelope<T> {
  data: T;
  generatedAt: string;
}

export interface PgStatsTable {
  name: string;
  estimatedRows: number;
  totalBytes: number;
  partitions: number;
}

export interface PgStatsPartition {
  name: string;
  parent: string;
  rows: number;
  size: string;
}

export interface PgStatsBuffer {
  pending: number;
  flushed: number;
  lastFlushAt: string | null;
}

export interface PgStatsUptime {
  startedAt: string;
  uptimeMs: number;
  growthBytes: number;
  growthPerHour: number;
}

export interface PgStats {
  databaseSize: string;
  databaseBytes: number;
  tables: PgStatsTable[];
  partitions: PgStatsPartition[];
  buffer: PgStatsBuffer;
  uptime: PgStatsUptime;
}

export type PgStatsResponse = AnalyticsAggregateEnvelope<PgStats>;

export interface GameSummary {
  gameKey: string;
  date: string;
  teams: string[];
  sport: string;
  markets: Array<{
    marketType: string;
    tickCount: number;
    sources: number;
    firstTick: string;
    lastTick: string;
  }>;
  trades: Array<{ source: string; tradeCount: number; totalSize: number }>;
}

export interface GamesResponse extends AnalyticsListEnvelope<GameSummary> {
  date: string;
}

export interface TradeRow {
  ts: string;
  ticker: string;
  source: string;
  game_key: string | null;
  market_type: string | null;
  line_key: string | null;
  sport: string | null;
  side: string;
  price: number;
  size: number;
  external_trade_id: string | null;
  asset_id: string | null;
  market_id: string | null;
  maker_address: string | null;
  taker_address: string | null;
  trader_side: string | null;
  tx_hash: string | null;
  count: number;
  taker_side: string | null;
}

export interface TradesResponse extends AnalyticsListEnvelope<TradeRow> {
  gameKey: string;
}

export type RecentTradesResponse = AnalyticsListEnvelope<TradeRow>;

export interface MarketSummary {
  ticker: string;
  source: string;
  sport: string | null;
  gameKey: string | null;
  marketType: string | null;
  tradeCount: number;
  totalSize: number;
  avgPrice: number;
  firstTradeAt: string;
  lastTradeAt: string;
}

export interface TradeSummaryResponse extends AnalyticsListEnvelope<MarketSummary> {
  date: string;
}

export interface HourlyBucket {
  hour: number;
  tradeCount: number;
  totalSize: number;
}

export interface HourlyActivity {
  date: string;
  sport: string | null;
  source: string | null;
  hours: HourlyBucket[];
}

export type HourlyActivityResponse = AnalyticsAggregateEnvelope<HourlyActivity>;

export interface SizeHistogramBin {
  lower: number;
  upper: number;
  count: number;
}

export interface SizeDistribution {
  date: string;
  buckets: number;
  totalTrades: number;
  min: number;
  max: number;
  median: number;
  bins: SizeHistogramBin[];
}

export type SizeDistributionResponse = AnalyticsAggregateEnvelope<SizeDistribution>;

export interface IntervalRange {
  label: string;
  lowerMs: number;
  upperMs: number | null;
  count: number;
}

export interface IntervalHourRow {
  hour: number;
  ranges: IntervalRange[];
}

export interface TradeIntervals {
  date: string;
  sport: string | null;
  source: string | null;
  hours: IntervalHourRow[];
}

export type TradeIntervalsResponse = AnalyticsAggregateEnvelope<TradeIntervals>;

export type LeaderboardWindow = '1d' | '7d' | '30d' | '90d' | 'all';
export type LeaderboardSort = 'winRate' | 'avgGainUsd' | 'gamesPlayed' | 'totalPnlUsd';

export interface OperatorLeaderboardRow {
  address: string;
  source: 'polymarket';
  gamesPlayed: number;
  wins: number;
  losses: number;
  winRate: number;
  totalPnlUsd: number;
  avgGainUsd: number;
  totalVolumeUsd: number;
}

export interface OperatorLeaderboardMeta {
  source: 'polymarket';
  window: LeaderboardWindow;
  minGames: number;
  sort: LeaderboardSort;
  order: 'desc' | 'asc';
}

export interface OperatorLeaderboardResponse
  extends AnalyticsListEnvelope<OperatorLeaderboardRow> {
  meta: OperatorLeaderboardMeta;
}

export interface CascadeOperator {
  id: string;
  label: string;
  volume: number;
  count: number;
  firstActiveAt?: string;
  lastActiveAt?: string;
}

export interface CascadeTrade {
  id: string;
  t: string; // ISO timestamp
  operatorId: string;
  marketId?: string | null;
  priceCents: number;
  size: number;
  side: 'BUY' | 'SELL' | string;
}

export interface CascadeRelationship {
  sourceTradeId: string;
  targetTradeId: string;
  lagMs: number;
  strength: number; // 0..1
}

export interface CascadeData {
  gameKey: string;
  operators: CascadeOperator[];
  trades: CascadeTrade[];
  relationships: CascadeRelationship[];
}

export type CascadeResponse = AnalyticsAggregateEnvelope<CascadeData>;

// ─── Typed wrappers ─────────────────────────────────────────────────

export function getPgStats(signal?: AbortSignal): Promise<PgStatsResponse> {
  return fetchAnalytics<PgStatsResponse>('/api/analytics/pg-stats', signal);
}

export function getGames(
  params: { date?: string; tz?: string } = {},
  signal?: AbortSignal,
): Promise<GamesResponse> {
  const qs = new URLSearchParams();
  if (params.date) qs.set('date', params.date);
  if (params.tz) qs.set('tz', params.tz);
  const suffix = qs.toString() ? `?${qs}` : '';
  return fetchAnalytics<GamesResponse>(`/api/analytics/games${suffix}`, signal);
}

export function getTrades(
  params: { gameKey: string; marketType?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<TradesResponse> {
  const qs = new URLSearchParams();
  qs.set('gameKey', params.gameKey);
  if (params.marketType) qs.set('marketType', params.marketType);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  return fetchAnalytics<TradesResponse>(`/api/analytics/trades?${qs}`, signal);
}

function buildQs(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    qs.set(k, String(v));
  }
  return qs.toString() ? `?${qs}` : '';
}

export function getRecentTrades(
  params: { sport?: string; source?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<RecentTradesResponse> {
  return fetchAnalytics<RecentTradesResponse>(`/api/analytics/recent-trades${buildQs(params)}`, signal);
}

export function getTradeSummary(
  params: { date?: string; sport?: string; source?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<TradeSummaryResponse> {
  return fetchAnalytics<TradeSummaryResponse>(`/api/analytics/trade-summary${buildQs(params)}`, signal);
}

export function getHourlyActivity(
  params: { date?: string; sport?: string; source?: string } = {},
  signal?: AbortSignal,
): Promise<HourlyActivityResponse> {
  return fetchAnalytics<HourlyActivityResponse>(`/api/analytics/hourly-activity${buildQs(params)}`, signal);
}

export function getSizeDistribution(
  params: { date?: string; sport?: string; source?: string; buckets?: number } = {},
  signal?: AbortSignal,
): Promise<SizeDistributionResponse> {
  return fetchAnalytics<SizeDistributionResponse>(`/api/analytics/size-distribution${buildQs(params)}`, signal);
}

export function getTradeIntervals(
  params: { date?: string; sport?: string; source?: string } = {},
  signal?: AbortSignal,
): Promise<TradeIntervalsResponse> {
  return fetchAnalytics<TradeIntervalsResponse>(`/api/analytics/trade-intervals${buildQs(params)}`, signal);
}

export function getOperatorLeaderboard(
  params: {
    window?: LeaderboardWindow;
    minGames?: number;
    sort?: LeaderboardSort;
    order?: 'desc' | 'asc';
    limit?: number;
    offset?: number;
  } = {},
  signal?: AbortSignal,
): Promise<OperatorLeaderboardResponse> {
  return fetchAnalytics<OperatorLeaderboardResponse>(
    `/api/analytics/operators/leaderboard${buildQs({ ...params, source: 'polymarket' })}`,
    signal,
  );
}

// ─── Orderbook Depth ────────────────────────────────────────────────

export interface DepthLevel {
  price: number;    // cents
  size: number;     // size at this level only
  cumSize: number;  // cumulative from L1 through this level
}

export interface DepthTick {
  ts: number;           // Unix ms
  levels: DepthLevel[]; // exactly 5 entries; index 0 = L1 (tightest/best)
}

export interface DepthSeries {
  ticks: DepthTick[]; // oldest → newest
}

export interface OrderbookDepthResponse {
  ticker: string;
  windowSecs: number;
  sampledAt: number;
  yes: DepthSeries;
  no: DepthSeries;
}

export function getOrderbookDepth(
  params: { ticker: string; windowSecs?: number },
  signal?: AbortSignal,
): Promise<OrderbookDepthResponse> {
  return fetchAnalytics<OrderbookDepthResponse>(
    `/api/analytics/orderbook-depth${buildQs(params)}`,
    signal,
  );
}

// ─── Playoff series round label ─────────────────────────────────────
// Used by ValueDashboard's series-row badge. The series matching itself
// flows through the live WS feed (canonicalSeriesKey on every series
// frame, collapsed in marketStore) — no analytics fetch needed for that.

const ROUND_LABELS: Record<'NBA' | 'NHL' | 'MLB', Record<number, string>> = {
  NBA: { 1: 'Round 1', 2: 'Round 2', 3: 'Conf Finals', 4: 'Finals' },
  NHL: { 1: 'Round 1', 2: 'Round 2', 3: 'Conf Finals', 4: 'Stanley Cup' },
  MLB: { 1: 'Wild Card', 2: 'LDS', 3: 'LCS', 4: 'World Series' },
};

export function roundLabel(sport: string, round: number): string {
  if (round === 0) return '—';
  const upper = sport.toUpperCase() as 'NBA' | 'NHL' | 'MLB';
  const map = ROUND_LABELS[upper];
  if (map?.[round]) return map[round];
  return `R${round}`;
}

// ─── Cross-Market Spread ─────────────────────────────────────────────

export interface CrossMarketMarket {
  gameKey: string;
  awayCode: string;
  homeCode: string;
  sport: string;
}

export interface CrossMarketMarketsResponse {
  ok: boolean;
  date: string;
  items: CrossMarketMarket[];
  total: number;
}

export interface CrossMarketSpreadRow {
  ts: string;
  kalshiMid: number | null;
  polyMid: number | null;
  rawDelta: number | null;
  netDelta: number | null;
}

export interface CrossMarketSpreadResponse {
  ok: boolean;
  gameKey: string;
  side: string;
  resolution: string;
  rows: CrossMarketSpreadRow[];
}

export function getCrossMarketSpreadMarkets(
  params: { date?: string; sport?: string } = {},
  signal?: AbortSignal,
): Promise<CrossMarketMarketsResponse> {
  return fetchAnalytics<CrossMarketMarketsResponse>(
    `/api/analytics/cross-market-spread/markets${buildQs(params)}`,
    signal,
  );
}

export function getCrossMarketSpread(
  params: {
    gameKey: string;
    side: string;
    resolution?: string;
    from?: string;
    to?: string;
    limit?: number;
  },
  signal?: AbortSignal,
): Promise<CrossMarketSpreadResponse> {
  return fetchAnalytics<CrossMarketSpreadResponse>(
    `/api/analytics/cross-market-spread${buildQs(params)}`,
  );
}

// Sport prefix in the gameKey causes upstream errors — normalize to the
// canonical `date|teamA|teamB` (alpha-sorted) shape via toHydraGameKey.
export function getCascade(
  params: { gameKey: string },
  signal?: AbortSignal,
): Promise<CascadeResponse> {
  const normalized = toHydraGameKey(params.gameKey) ?? params.gameKey;
  return fetchAnalytics<CascadeResponse>(
    `/api/analytics/operators/cascade/${encodeURIComponent(normalized)}`,
    signal,
  );
}

export interface OperatorPlayByPlayFill {
  wallet: string;
  ts: number;
  priceCents: number;
  sizeUsd: number;
  side: 'yes' | 'no';
  txHash: string;
}

export interface OperatorPlayByPlayOperator {
  wallet: string;
  fillCount: number;
  totalSizeUsd: number;
  firstTs: number;
  lastTs: number;
}

export interface OperatorPlayByPlayResponse {
  fills: OperatorPlayByPlayFill[];
  operators: OperatorPlayByPlayOperator[];
  meta: {
    gameKey: string;
    windowFrom: number | null;
    windowTo: number | null;
    referenceTeam: string | null;
  };
}

export function getOperatorPlayByPlay(
  params: { gameKey: string },
  signal?: AbortSignal,
): Promise<OperatorPlayByPlayResponse> {
  const normalized = toHydraGameKey(params.gameKey) ?? params.gameKey;
  return fetchAnalytics<OperatorPlayByPlayResponse>(
    `/api/analytics/operators/play-by-play/${encodeURIComponent(normalized)}`,
    signal,
  );
}
